import React, { useEffect, useState } from 'react';
import {
  AlertTriangle,
  Eye,
  Image as ImageIcon,
  KeyRound,
  Link2,
  PanelLeftOpen,
  RefreshCw,
  Send,
  Sparkles,
  Star,
  Trash2,
  X
} from 'lucide-react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { selectedClientSelector } from '../store/slices/selectedClient';

const suggestions = [
  { icon: AlertTriangle, label: 'Pending tasks' },
  { icon: Eye, label: 'Clients with no content' },
  { icon: Star, label: 'Unanswered reviews' },
  { icon: Link2, label: 'Connect GBP' },
  { icon: RefreshCw, label: 'Review FB connection' },
  { icon: Trash2, label: 'Delete access' },
  { icon: KeyRound, label: 'Reset dashboard password' },
  { icon: ImageIcon, label: 'Sync image gallery' }
];

// Example questions for the global (all clients) mode, shown while the conversation is empty
const globalSuggestions = [
  { icon: Eye, label: 'Clients with no content' },
  { icon: Star, label: 'Unanswered reviews' },
  { icon: RefreshCw, label: 'Review FB connection' },
  { icon: KeyRound, label: 'Reset dashboard password' }
];

const initialMessages = [
  {
    id: 1,
    role: 'assistant',
    text: "Hi, I'm the AdlerLocal assistant. I can check the post calendar, the Google Business profile, and reviews. How can I help?"
  }
];

const globalInitialMessages = [
  {
    id: 1,
    role: 'assistant',
    text: "Hi, I'm the AdlerLocal assistant. I'm connected to all clients and can check the daily posting log for Facebook, Google and Instagram. How can I help?"
  }
];

const CHAT_ENDPOINT =
  'https://qc5l7t2grxvh2zdywqgjyerini0myajk.lambda-url.us-east-1.on.aws/';
const CHAT_KEY = 'TOQOvIkX3tXjbZKh6u1Isq0zSfvOrknIuoA5WMbl';
const GLOBAL_SCOPE = 'global';
// The global view lives on the clients list page
const GLOBAL_PATHS = ['/clients'];
const CLIENT_TIMEOUT_MS = 65000;
// Global answers go through every client's data and can take up to 60s
const GLOBAL_TIMEOUT_MS = 120000;

// Global and client sessions must never be mixed, so each scope has its own key
const getSessionStorageKey = (scope) => `chatSession:${scope}`;

const markdownComponents = {
  table: ({ node, ...props }) => (
    <div className="max-w-full overflow-x-auto">
      <table {...props} />
    </div>
  )
};

const buildErrorMessage = (error, fallbackText) => {
  if (error?.status === 401) {
    return 'Configuration error: unauthorized. Check the frontend X-Chat-Key header.';
  }

  if (error?.status === 502) {
    return `${
      error.message || 'I could not respond. Would you like to try again?'
    }\n\nYou can retry with the button below.`;
  }

  return error?.message || fallbackText;
};

const requestAssistant = async (
  message,
  sessionId,
  { clientId, isGlobal = false } = {}
) => {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(
    () => controller.abort(),
    isGlobal ? GLOBAL_TIMEOUT_MS : CLIENT_TIMEOUT_MS
  );

  try {
    const response = await fetch(CHAT_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Chat-Key': CHAT_KEY
      },
      body: JSON.stringify(
        isGlobal
          ? { message, sessionId: sessionId || '', global: true }
          : {
              // The clientId travels in the message for the model but is not shown in the chat
              message: clientId
                ? `[clientId: ${clientId}]\n${message}`
                : message,
              sessionId: sessionId || '',
              clientId: clientId || ''
            }
      ),
      signal: controller.signal
    });

    if (!response.ok) {
      const data = await response
        .json()
        .catch(() => ({ error: 'Unknown error.' }));
      const apiError = new Error(data.error || 'Unknown error.');
      apiError.status = response.status;
      throw apiError;
    }

    return response.json();
  } catch (error) {
    if (error.name === 'AbortError') {
      const timeoutError = new Error(
        'The response took longer than expected. Can you try again?'
      );
      timeoutError.status = 504;
      throw timeoutError;
    }

    throw error;
  } finally {
    window.clearTimeout(timeoutId);
  }
};

const DashboardInsightsPanel = () => {
  const { selectedClient } = useSelector(selectedClientSelector);
  const { pathname } = useLocation();
  const isGlobal = GLOBAL_PATHS.includes(pathname);
  const clientId = selectedClient?.id;
  const scope = isGlobal ? GLOBAL_SCOPE : `client:${clientId || ''}`;
  const scopeInitialMessages = isGlobal
    ? globalInitialMessages
    : initialMessages;

  const [isOpen, setIsOpen] = useState(false);
  // Conversations are kept per scope so switching pages never mixes them
  const [conversations, setConversations] = useState({});
  const [pendingScopes, setPendingScopes] = useState([]);
  const [draft, setDraft] = useState('');

  const messages = conversations[scope] || scopeInitialMessages;
  const thinking = pendingScopes.includes(scope);
  const isConversationEmpty = !messages.some(
    (message) => message.role === 'user'
  );

  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'i') {
        event.preventDefault();
        setIsOpen((open) => !open);
      }

      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    const styleId = 'dashboard-insights-hide-chatbase';
    document.getElementById(styleId)?.remove();

    if (!isOpen) return undefined;

    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      #chatbase-bubble-button,
      #chatbase-bubble-window,
      #chatbase-message-bubbles,
      [id^="chatbase"],
      iframe[src*="chatbase"],
      iframe[id*="chatbase"],
      div[class*="chatbase"] {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
      }
    `;
    document.head.appendChild(style);

    return () => document.getElementById(styleId)?.remove();
  }, [isOpen]);

  const appendMessage = (targetScope, fallbackMessages, message) => {
    setConversations((current) => ({
      ...current,
      [targetScope]: [...(current[targetScope] || fallbackMessages), message]
    }));
  };

  const pushAssistantMessage = (
    payload,
    targetScope = scope,
    fallbackMessages = scopeInitialMessages
  ) => {
    appendMessage(targetScope, fallbackMessages, {
      id: Date.now() + Math.floor(Math.random() * 1000),
      role: 'assistant',
      text: payload.text,
      isError: payload.isError,
      retryText: payload.retryText
    });
  };

  // Only clears the conversation and session of the current mode
  const resetConversation = () => {
    setConversations((current) => {
      const next = { ...current };
      delete next[scope];
      return next;
    });
    localStorage.removeItem(getSessionStorageKey(scope));
  };

  const sendMessage = async (value) => {
    const text = value.trim();
    if (!text || thinking) return;

    if (text.length > 2000) {
      pushAssistantMessage({
        text: 'Your message exceeds the 2000-character limit. Please shorten it and try again.',
        isError: true
      });
      return;
    }

    // Capture the scope so a slow reply lands in the conversation that asked for it
    const requestScope = scope;
    const requestInitialMessages = scopeInitialMessages;
    const sessionStorageKey = getSessionStorageKey(requestScope);

    appendMessage(requestScope, requestInitialMessages, {
      id: Date.now(),
      role: 'user',
      text
    });
    setDraft('');
    setPendingScopes((current) => [...current, requestScope]);

    try {
      const data = await requestAssistant(
        text,
        localStorage.getItem(sessionStorageKey) || '',
        { clientId, isGlobal }
      );

      if (data?.sessionId) {
        localStorage.setItem(sessionStorageKey, data.sessionId);
      }

      pushAssistantMessage(
        { text: data?.reply || 'I could not generate a response.' },
        requestScope,
        requestInitialMessages
      );
    } catch (error) {
      const friendlyMessage = buildErrorMessage(
        error,
        'I could not respond right now. Would you like to try again?'
      );

      pushAssistantMessage(
        {
          text: friendlyMessage,
          isError: true,
          retryText: error?.status === 502 ? text : undefined
        },
        requestScope,
        requestInitialMessages
      );
    } finally {
      setPendingScopes((current) =>
        current.filter((pendingScope) => pendingScope !== requestScope)
      );
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    sendMessage(draft);
  };

  return (
    <>
      {!isOpen && (
        <button
          type="button"
          onClick={() => setIsOpen(true)}
          aria-label="Open platform assistant"
          className="fixed right-0 top-1/2 z-[60] flex -translate-y-1/2 items-center justify-center rounded-l-xl border border-r-0 border-border bg-card px-2 py-4 text-muted-foreground shadow-lg transition-all hover:px-3 hover:text-foreground">
          <PanelLeftOpen className="h-4 w-4" />
        </button>
      )}

      <aside
        aria-hidden={!isOpen}
        className={`fixed right-0 top-0 z-[70] flex h-full w-[400px] max-w-[calc(100vw-24px)] min-w-0 flex-col border-l border-border bg-card shadow-2xl transition-transform duration-300 max-md:max-w-full ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}>
        <div className="flex items-start justify-between gap-3 border-b border-border px-4 py-3">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10">
              <Sparkles className="h-5 w-5 text-primary" />
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-foreground">
                {isGlobal ? 'Admin All Clients' : 'Dashboard Insights'}
              </p>
              {isGlobal ? (
                <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary" />
                  Connected to all clients
                </p>
              ) : (
                <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-primary" />
                  Connected to your live data
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={resetConversation}
              aria-label="Start new conversation"
              className="rounded-md px-2 py-1 text-xs text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
              New conversation
            </button>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              aria-label="Close panel"
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {(!isGlobal || isConversationEmpty) && (
          <div className="flex flex-wrap gap-2 border-b border-border px-4 py-3">
            {(isGlobal ? globalSuggestions : suggestions).map(
              ({ icon: Icon, label }) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => sendMessage(label)}
                  className="flex items-center gap-1.5 rounded-full border border-border bg-background px-3 py-1.5 text-left text-xs text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground">
                  <Icon className="h-3.5 w-3.5 shrink-0" />
                  {label}
                </button>
              )
            )}
          </div>
        )}

        <div className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${
                message.role === 'user' ? 'justify-end' : 'justify-start'
              }`}>
              <div
                className={`min-w-0 max-w-[85%] rounded-lg px-3 py-2 text-sm leading-relaxed ${
                  message.role === 'user'
                    ? 'bg-primary text-primary-foreground'
                    : message.isError
                    ? 'border border-destructive/30 bg-destructive/10 text-foreground'
                    : 'border border-border bg-muted text-foreground'
                }`}>
                {message.role === 'assistant' ? (
                  <div className="space-y-2">
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={markdownComponents}
                      className="space-y-2 break-words [&_h1]:text-base [&_h1]:font-semibold [&_h2]:text-base [&_h2]:font-semibold [&_h3]:text-sm [&_h3]:font-semibold [&_ol]:list-decimal [&_ol]:pl-5 [&_ul]:list-disc [&_ul]:pl-5 [&_table]:w-full [&_table]:border-collapse [&_td]:border [&_td]:border-border [&_td]:px-2 [&_td]:py-1 [&_td]:align-top [&_th]:whitespace-nowrap [&_th]:border [&_th]:border-border [&_th]:bg-background [&_th]:px-2 [&_th]:py-1 [&_p]:whitespace-pre-wrap">
                      {message.text}
                    </ReactMarkdown>
                    {message.retryText && (
                      <button
                        type="button"
                        disabled={thinking}
                        onClick={() => sendMessage(message.retryText)}
                        className="rounded-md border border-border bg-background px-2 py-1 text-xs text-muted-foreground transition-colors hover:text-foreground disabled:cursor-not-allowed disabled:opacity-60">
                        Retry
                      </button>
                    )}
                  </div>
                ) : (
                  message.text
                )}
              </div>
            </div>
          ))}
          {thinking && (
            <div className="text-sm text-muted-foreground">Typing...</div>
          )}
        </div>

        <form
          onSubmit={handleSubmit}
          className="border-t border-border p-3 pb-16">
          <div className="rounded-lg border border-border bg-background p-2">
            <textarea
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  event.currentTarget.form.requestSubmit();
                }
              }}
              rows={3}
              maxLength={2000}
              disabled={thinking}
              placeholder="Ask the dashboard..."
              aria-label="Ask the dashboard"
              className="w-full resize-none border-0 bg-transparent px-1 py-1 text-sm text-foreground outline-none placeholder:text-muted-foreground"
            />
            <div className="flex justify-end">
              <button
                type="submit"
                disabled={!draft.trim() || thinking}
                aria-label="Send message"
                className="flex h-8 w-8 items-center justify-center rounded-md bg-primary text-primary-foreground transition-opacity disabled:cursor-not-allowed disabled:opacity-50">
                <Send className="h-4 w-4" />
              </button>
            </div>
          </div>
        </form>
      </aside>
    </>
  );
};

export default DashboardInsightsPanel;
